// The phone's widgets: small live views of the app on the home screens. Each
// reads the same hooks as the page it opens (no data of its own) and follows
// the house rules: names, never counts of what is undone; a tick is a real
// action with undo; nothing shows a loading state for synced data.
import { PrimeMark } from '@/components/PrimeMark'
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'
import {
  CalendarDays,
  CalendarHeart,
  CloudSun,
  Clock3,
  Gamepad2,
  Headphones,
  HeartPulse,
  Hammer,
  Inbox,
  Lightbulb,
  ListMusic,
  ListChecks,
  MicVocal,
  Music,
  Pause,
  Play,
  SkipForward,
  Sun,
  Timer,
} from 'lucide-react'
import {
  useAllLists,
  useAllTasks,
  useDates,
  useDays,
  useSettings,
  useTasks,
  useTasksByState,
} from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { selectToday } from '@/lib/today'
import { dayShapeParts } from '@/lib/shape'
import { addDays, dateOf, fmtDateShort, todayLocal } from '@/lib/time'
import { taskActions } from '@/lib/actions'
import { isStale, openJobs } from '@/lib/jobs'
import { eveningOf, logSmoke, summarise } from '@/lib/wellbeing'
import { nextFixed, rowLineAt, upcomingDates } from '@/lib/dates'
import { DateLeaf } from '@/components/DateBits'
import { isOpen, isTemplate } from '@/kinds/task/schema'
import { sortLists } from '@/kinds/list/components'
import type { TaskResource } from '@/kinds/task/apply'
import { mutate } from '@/runtime'
import { usePomodoro, PHASE_LABEL, phaseMinutes } from '@/state/pomodoro'
import { usePlayer } from '@/state/player'
import { usePomodoroClock } from '@/components/Pomodoro'
import { cx } from '@/components/ui'
import type { WidgetKind, WidgetSize } from './layout'
import { useLead } from './AskPrime'
import { leadLast } from '@/lib/lead'
import { useRunPhase } from '@/kinds/agentrun/components'
import { runStatus } from '@/lib/waiting'
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import { GamesWidget, MusicWidget } from './statsWidgets'
import { HouseWidget, LyricsWidget, UpNextWidget, WeatherWidget } from './outsideWidgets'

export const WIDGETS: Record<
  WidgetKind,
  { name: string; blurb: string; Icon: ComponentType<LucideProps>; to: string }
> = {
  clock: { name: 'Clock', blurb: 'The time, the date and the shape of the day', Icon: Clock3, to: '/' },
  today: { name: 'Today', blurb: 'What is next, with a tick for each', Icon: Sun, to: '/' },
  upcoming: { name: 'Upcoming', blurb: 'The next few days', Icon: CalendarDays, to: '/upcoming' },
  inbox: { name: 'Inbox', blurb: 'The newest things waiting to be filed', Icon: Inbox, to: '/inbox' },
  focus: { name: 'Focus', blurb: 'The pomodoro, with start and pause', Icon: Timer, to: '/focus' },
  nowplaying: {
    name: 'Now playing',
    blurb: 'What Spotify is playing, with the remote',
    Icon: Music,
    to: '/music',
  },
  prime: {
    name: 'Prime',
    blurb: 'What Prime last said and what it is doing, and a way to ask',
    Icon: PrimeMark,
    to: '/agents',
  },
  wellbeing: {
    name: 'Wellbeing',
    blurb: 'A smoke in one tap, and the evening’s next step',
    Icon: HeartPulse,
    to: '/wellbeing',
  },
  list: { name: 'List', blurb: 'A list’s open items, tickable', Icon: ListChecks, to: '/lists' },
  music: {
    name: 'Music',
    blurb: 'This week’s listening, who it was, and the week’s shape',
    Icon: Headphones,
    to: '/music/insights',
  },
  dates: {
    name: 'Coming up',
    blurb: 'Birthdays, appointments and renewals ahead',
    Icon: CalendarHeart,
    to: '/dates',
  },
  jobs: {
    name: 'House jobs',
    blurb: 'Jobs around the house that are due, with a Done for each',
    Icon: Hammer,
    to: '/inbox',
  },
  games: {
    name: 'Games',
    blurb: 'This week’s Steam play, the game, and the week’s shape',
    Icon: Gamepad2,
    to: '/music/insights',
  },
  // the outside widgets (os/outsideWidgets.tsx); their place and entities are chosen in Settings → Home screen
  weather: {
    name: 'Weather',
    blurb: 'Now and the next few days, for the place chosen in Settings',
    Icon: CloudSun,
    to: '/settings/home',
  },
  house: {
    name: 'House',
    blurb: 'Home Assistant: lights and switches to tap, sensors to read',
    Icon: Lightbulb,
    to: '/settings/home',
  },
  lyrics: {
    name: 'Lyrics',
    blurb: 'The words of what is playing, line by line',
    Icon: MicVocal,
    to: '/music',
  },
  upnext: { name: 'Up next', blurb: 'What Spotify plays after this', Icon: ListMusic, to: '/music' },
}

/** The card every widget sits in: a header that opens the app, then the body. */
export const Frame = ({
  kind,
  title,
  to,
  search,
  editing,
  children,
  className,
  right,
  size,
  footer,
}: {
  kind: WidgetKind
  title?: string
  to?: string
  /** query for the page the header opens (Insights on a metric) */
  search?: Record<string, string>
  editing: boolean
  children: ReactNode
  className?: string
  right?: ReactNode
  /** large: a way on at the foot ("Open Groceries"), so a short list does not leave half the widget blank */
  size?: WidgetSize
  footer?: string
}) => {
  const navigate = useNavigate()
  const w = WIDGETS[kind]
  const open = () => void navigate({ to: to ?? w.to, search: search as never })
  return (
    <div
      className={cx(
        'os-widget h-full w-full rounded-[var(--radius-card)] border border-line/70 bg-panel/[0.88] p-3 flex flex-col min-h-0 overflow-hidden shadow-[0_10px_30px_-18px_rgba(0,0,0,0.8)]',
        editing && 'pointer-events-none',
        className,
      )}
      data-widget={kind}
    >
      <div className="flex items-center gap-1.5 mb-1.5 shrink-0">
        <button
          type="button"
          onClick={open}
          className="flex items-center gap-1.5 min-w-0 text-[13px] font-semibold text-muted hover:text-fg"
        >
          <w.Icon size={13} className="shrink-0 text-accent" aria-hidden />
          <span className="truncate">{title ?? w.name}</span>
        </button>
        <span className="flex-1" />
        {right}
      </div>
      {/* a large widget's rows stop above its way on, never under it */}
      <div className={cx('flex-1 min-h-0 flex flex-col', size === 'large' && 'overflow-hidden')}>
        {children}
      </div>
      {size === 'large' && (
        <button
          type="button"
          onClick={open}
          className="shrink-0 mt-2 self-start rounded-full bg-raised border border-line px-3 min-h-8 text-xs text-fg-2"
          data-testid="widget-footer"
        >
          {footer ?? `Open ${title ?? w.name}`}
        </button>
      )}
    </div>
  )
}

const Tick = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-line-strong hover:border-accent hover:bg-accent-soft"
  />
)

const TaskLine = ({ t }: { t: TaskResource }) => (
  <li className="flex items-center gap-2 min-h-7">
    <Tick onClick={() => void taskActions.complete(t)} label={`Complete ${t.spec.title}`} />
    <span className="truncate text-[13px] leading-5">{t.spec.title}</span>
  </li>
)

export const Quiet = ({ children }: { children: ReactNode }) => (
  <p className="text-[13px] text-faint flex-1 flex items-center">{children}</p>
)

// ---- the widgets ---------------------------------------------------------------

const ClockWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const now = useNow(30_000)
  const tasks = useTasks() ?? []
  const settings = useSettings()
  const sel = selectToday(tasks, {
    today: todayLocal(),
    cap: settings.today_cap,
    picker: { time: 'any', energy: 'any' },
    activeId: null,
  })
  const upNext = nextFixed(useDates(), new Date(now))
  const shape = dayShapeParts(
    sel.all,
    now,
    settings.recap_push,
    upNext && { title: upNext.fixed.date.spec.title, minutes: upNext.minutes },
  )
  const d = new Date(now)
  return (
    <Frame kind="clock" editing={editing} title={d.toLocaleDateString(undefined, { weekday: 'long' })}>
      <div
        className={cx(
          'font-semibold tracking-tight tabular-nums leading-none',
          size === 'small' ? 'text-4xl' : 'text-5xl',
        )}
      >
        {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div className="text-xs text-muted mt-1.5">
        {d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}
      </div>
      {/* the phrases set apart by space, as Today has them, never joined by dots */}
      {size !== 'small' && shape && (
        <p className="mt-auto flex flex-wrap gap-x-3 h-4 overflow-hidden text-xs text-faint">
          {shape.map((part) => (
            <span key={part} className="whitespace-nowrap">
              {part}
            </span>
          ))}
        </p>
      )}
    </Frame>
  )
}

const TodayWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const tasks = useTasks() ?? []
  const settings = useSettings()
  const sel = selectToday(tasks, {
    today: todayLocal(),
    cap: settings.today_cap,
    picker: { time: 'any', energy: 'any' },
    activeId: null,
  })
  const shown = sel.all.slice(0, size === 'large' ? 8 : 3)
  const first = shown[0]
  return (
    <Frame
      kind="today"
      size={size}
      footer="Open Today"
      editing={editing}
      right={
        first ? (
          <button
            type="button"
            onClick={() => void taskActions.start(first)}
            className="text-[11px] rounded-full bg-accent text-on-accent px-2.5 min-h-6 inline-flex items-center gap-1"
          >
            <Play size={10} aria-hidden /> Start
          </button>
        ) : undefined
      }
    >
      {shown.length ? (
        <ul className="flex flex-col">
          {shown.map((t) => (
            <TaskLine key={t.id} t={t} />
          ))}
        </ul>
      ) : (
        <Quiet>Nothing planned for today.</Quiet>
      )}
    </Frame>
  )
}

// The house jobs, the soonest due first (the stale ones lead and read brighter), a Done
// on each, so a job can be done before its nudge. Every workspace, as the push is; the
// day turns over by itself on a Home left open. No totals, no overdue counts.
const JobsWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const now = useNow(60_000)
  const today = dateOf(new Date(now))
  const jobs = openJobs(useAllTasks() ?? []).slice(0, size === 'small' ? 2 : 3)
  return (
    <Frame kind="jobs" editing={editing}>
      {jobs.length ? (
        <ul className="flex flex-col">
          {jobs.map((t) => (
            <li key={t.id} className="flex items-center gap-2 min-h-7" data-stale={isStale(t, today)}>
              <span
                className={cx('truncate text-[13px] leading-5 flex-1', !isStale(t, today) && 'text-muted')}
              >
                {t.spec.title}
              </span>
              <button
                type="button"
                onClick={() => void taskActions.doneNow(t)}
                aria-label={`Done: ${t.spec.title}`}
                className="text-[11px] rounded-full bg-accent text-on-accent px-2.5 min-h-6 shrink-0"
              >
                Done
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <Quiet>Nothing to do around the house.</Quiet>
      )}
    </Frame>
  )
}

const UpcomingWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const today = todayLocal()
  const end = addDays(today, 14)
  const tasks = (useTasks() ?? [])
    .filter(
      (t) =>
        isOpen(t.status.state) &&
        !isTemplate(t.spec) &&
        !!t.spec.scheduled &&
        t.spec.scheduled > today &&
        t.spec.scheduled <= end,
    )
    .sort((a, b) =>
      a.spec.scheduled! < b.spec.scheduled! ? -1 : a.spec.scheduled! > b.spec.scheduled! ? 1 : 0,
    )
    .slice(0, size === 'large' ? 8 : 3)
  return (
    <Frame kind="upcoming" size={size} footer="Open the calendar" editing={editing}>
      {tasks.length ? (
        <ul className="flex flex-col">
          {tasks.map((t) => (
            <li key={t.id} className="flex items-baseline gap-2 min-h-7">
              <span className="w-14 shrink-0 text-[11px] text-accent-text truncate">
                {fmtDateShort(t.spec.scheduled!, today)}
              </span>
              <span className="truncate text-[13px] leading-5">{t.spec.title}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Quiet>Nothing in the next two weeks.</Quiet>
      )}
    </Frame>
  )
}

/** The next dates: a leaf and a name each; small shows the soonest alone, big. */
const DatesWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const now = useNow(60_000)
  const today = todayLocal()
  const at = new Date(now)
  const minute = at.getHours() * 60 + at.getMinutes()
  // an appointment that is on says so ("Now, until 11:00"); one that is over has gone
  const coming = upcomingDates(useDates(), today, 60)
    .flatMap((u) => {
      const line = rowLineAt(u.date.spec, u.on, today, minute)
      return line === null ? [] : [{ ...u, line }]
    })
    .slice(0, size === 'large' ? 6 : 3)
  const first = coming[0]
  return (
    <Frame kind="dates" size={size} footer="All dates" editing={editing}>
      {!first ? (
        <Quiet>Nothing in the next two months.</Quiet>
      ) : size === 'small' ? (
        <div className="flex-1 flex flex-col justify-end gap-1.5 min-w-0">
          <DateLeaf on={first.on} category={first.date.spec.category} small />
          <span className="text-[13px] leading-5 font-medium truncate">{first.date.spec.title}</span>
          <span className="text-[11px] text-faint truncate -mt-1">{first.line}</span>
        </div>
      ) : (
        <ul className="flex flex-col gap-1">
          {coming.map((u) => (
            <li key={u.date.id} className="flex items-center gap-2 min-w-0">
              <DateLeaf on={u.on} category={u.date.spec.category} small />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] leading-4 truncate">{u.date.spec.title}</span>
                <span className="block text-[11px] text-faint truncate">{u.line}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Frame>
  )
}

const InboxWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const tasks = (useTasksByState('inbox') ?? [])
    .filter((t) => !isTemplate(t.spec))
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, size === 'small' ? 3 : 4)
  return (
    <Frame kind="inbox" editing={editing}>
      {tasks.length ? (
        <ul className="flex flex-col gap-1">
          {tasks.map((t) => (
            <li key={t.id} className="truncate text-[13px] leading-5">
              {t.spec.title}
            </li>
          ))}
        </ul>
      ) : (
        <Quiet>Clear.</Quiet>
      )}
    </Frame>
  )
}

const FocusWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const left = usePomodoroClock()
  const p = usePomodoro()
  const mm = Math.floor(left / 60_000)
  const ss = Math.floor((left % 60_000) / 1000)
  // the full-screen dial carries its own label and rounds; at widget size a bar says the same
  const total = phaseMinutes(p.config, p.phase) * 60_000
  const done = total ? Math.min(1, Math.max(0, 1 - left / total)) : 0
  const toggle = () => (p.status === 'running' ? p.pause() : p.start())
  return (
    <Frame kind="focus" editing={editing} title={PHASE_LABEL[p.phase]}>
      <div className="flex-1 flex flex-col justify-end gap-2">
        <div className="flex items-end justify-between gap-2">
          <span className="text-3xl font-semibold tabular-nums leading-none">
            {mm}:{String(ss).padStart(2, '0')}
          </span>
          {size !== 'small' && <span className="text-xs truncate text-muted">{p.title ?? 'No task'}</span>}
        </div>
        <div className="h-1 rounded-full bg-raised overflow-hidden" aria-hidden>
          <div
            className="h-full bg-accent rounded-full transition-[width]"
            style={{ width: `${done * 100}%` }}
          />
        </div>
        <button
          type="button"
          onClick={toggle}
          className="self-start rounded-full bg-raised border border-line px-3 min-h-8 text-xs inline-flex items-center gap-1.5"
          data-testid="widget-focus-toggle"
        >
          {p.status === 'running' ? <Pause size={11} aria-hidden /> : <Play size={11} aria-hidden />}
          {p.status === 'running' ? 'Pause' : p.status === 'paused' ? 'Resume' : 'Start'}
        </button>
      </div>
    </Frame>
  )
}

const NowPlayingWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const st = usePlayer((s) => s.state)
  const watch = usePlayer((s) => s.watch)
  const control = usePlayer((s) => s.control)
  useEffect(() => watch(), [watch])
  const on = !!st?.connected && !!st.title
  return (
    <Frame kind="nowplaying" editing={editing}>
      {!on ? (
        <Quiet>{st?.connected ? 'Nothing playing.' : 'Spotify is not connected.'}</Quiet>
      ) : (
        <div className={cx('flex-1 min-h-0 flex gap-3', size === 'small' ? 'flex-col' : 'items-center')}>
          {st.image && (
            <img
              src={st.image}
              alt=""
              className={cx('rounded-lg object-cover shrink-0', size === 'small' ? 'h-12 w-12' : 'h-16 w-16')}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium">{st.title}</div>
            <div className="truncate text-xs text-muted">{st.artist}</div>
            {size !== 'small' && (
              <div className="flex items-center gap-1 mt-1.5">
                <button
                  type="button"
                  aria-label={st.playing ? 'Pause' : 'Play'}
                  onClick={() => void control(st.playing ? 'pause' : 'play')}
                  className="h-8 w-8 rounded-full bg-accent text-on-accent flex items-center justify-center"
                >
                  {st.playing ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => void control('next')}
                  className="h-8 w-8 rounded-full text-fg-2 flex items-center justify-center hover:bg-raised"
                >
                  <SkipForward size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Frame>
  )
}

/** Prime's state in a word (working, waiting for you, idle), from the live phase when it is newer. */
const LeadPhase = ({ lead }: { lead: AgentRunResource }) => {
  const phase = useRunPhase(lead)
  return (
    <span className="text-[11px] text-faint truncate" data-testid="widget-prime-phase">
      {runStatus(phase, undefined, true).word}
    </span>
  )
}

// Prime is the lead (lib/lead.ts): its last words and its state; a tap opens its thread
// the Ask Prime bar sits under Home's widgets: the widget is Prime's last words and a way into its thread
const PrimeWidget = ({ editing }: { editing: boolean }) => {
  const lead = useLead()
  const navigate = useNavigate()
  const last = leadLast(lead)
  const to = lead ? `/agents/run/${lead.id}` : '/agents'
  return (
    <Frame kind="prime" editing={editing} to={to} right={lead && <LeadPhase lead={lead} />}>
      <button
        type="button"
        onClick={() => void navigate({ to })}
        className="flex-1 min-h-0 flex items-start text-left text-[13px] leading-5 text-fg-2"
        data-testid="widget-prime-last"
      >
        <span className="line-clamp-2">{lead ? (last ?? 'Nothing said yet.') : "Prime isn't running."}</span>
      </button>
    </Frame>
  )
}

const WellbeingWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const days = useDays()
  const settings = useSettings()
  const navigate = useNavigate()
  const now = useNow(60_000)
  const date = eveningOf(now)
  const rec = days.find((d) => d.spec.date === date)?.spec
  const s = rec ? summarise(rec) : undefined
  const next = !rec?.ended_at
    ? { to: '/wellbeing', label: 'End of day' }
    : !s?.resetDone
      ? { to: '/reset', label: 'The chair' }
      : !s.checkedIn
        ? { to: '/checkin', label: 'Check in' }
        : null
  // one line of where the evening stands, said plainly (never a count of anything missed)
  const latest = [...(rec?.smokes ?? [])].sort((a, b) => a.at.localeCompare(b.at)).at(-1)
  const clockOf = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const line = rec?.checked_in_at
    ? `Checked in at ${clockOf(rec.checked_in_at)}`
    : rec?.ended_at
      ? `Day ended at ${clockOf(rec.ended_at)}`
      : latest
        ? `Last at ${clockOf(latest.at)}`
        : undefined
  return (
    <Frame kind="wellbeing" editing={editing}>
      {line && (
        <p className="text-[13px] text-fg-2 truncate" data-testid="widget-wellbeing-line">
          {line}
        </p>
      )}
      <div className={cx('flex-1 flex gap-2', size === 'small' ? 'flex-col justify-end' : 'items-end')}>
        <button
          type="button"
          onClick={() => void logSmoke(date)}
          className="rounded-full bg-raised border border-line px-3 min-h-8 text-xs"
          data-testid="widget-smoke"
        >
          {s && s.smokeTotal > 0
            ? `${s.smokeTotal} ${settings.smoke_unit}${s.smokeTotal === 1 ? '' : 's'}`
            : 'Smoke'}
        </button>
        {next ? (
          <button
            type="button"
            onClick={() => void navigate({ to: next.to })}
            className="rounded-full bg-raised border border-line px-3 min-h-8 text-xs"
          >
            {next.label}
          </button>
        ) : (
          <span className="text-xs text-muted">Tonight’s done</span>
        )}
      </div>
    </Frame>
  )
}

const ListWidget = ({ size, editing, listId }: { size: WidgetSize; editing: boolean; listId?: string }) => {
  const lists = sortLists((useAllLists() ?? []).filter((l) => !l.deleted_at && !l.status.converted_to))
  const list = lists.find((l) => l.id === listId) ?? lists.find((l) => l.spec.pinned) ?? lists[0]
  const open = (list?.spec.items ?? [])
    .filter((i) => !i.done)
    .slice(0, size === 'large' ? 10 : size === 'wide' ? 4 : 3)
  return (
    <Frame
      kind="list"
      size={size}
      editing={editing}
      title={list?.spec.title ?? 'List'}
      to={list ? `/lists/${list.id}` : '/lists'}
    >
      {!list ? (
        <Quiet>No lists yet.</Quiet>
      ) : open.length ? (
        <ul className="flex flex-col">
          {open.map((it) => (
            <li key={it.id} className="flex items-center gap-2 min-h-7">
              <Tick
                onClick={() => void mutate.action(list.id, 'tick', { id: it.id })}
                label={`Tick ${it.text}`}
              />
              <span className="truncate text-[13px] leading-5">{it.text}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Quiet>All ticked.</Quiet>
      )}
    </Frame>
  )
}

export const Widget = ({
  kind,
  size,
  refId,
  editing,
}: {
  kind: WidgetKind
  size: WidgetSize
  refId?: string
  editing: boolean
}) => {
  switch (kind) {
    case 'clock':
      return <ClockWidget size={size} editing={editing} />
    case 'today':
      return <TodayWidget size={size} editing={editing} />
    case 'upcoming':
      return <UpcomingWidget size={size} editing={editing} />
    case 'inbox':
      return <InboxWidget size={size} editing={editing} />
    case 'focus':
      return <FocusWidget size={size} editing={editing} />
    case 'nowplaying':
      return <NowPlayingWidget size={size} editing={editing} />
    case 'prime':
      return <PrimeWidget editing={editing} />
    case 'wellbeing':
      return <WellbeingWidget size={size} editing={editing} />
    case 'list':
      return <ListWidget size={size} editing={editing} listId={refId} />
    case 'music':
      return <MusicWidget size={size} editing={editing} />
    case 'games':
      return <GamesWidget size={size} editing={editing} />
    case 'dates':
      return <DatesWidget size={size} editing={editing} />
    case 'jobs':
      return <JobsWidget size={size} editing={editing} />
    case 'weather':
      return <WeatherWidget size={size} editing={editing} />
    case 'house':
      return <HouseWidget size={size} editing={editing} />
    case 'lyrics':
      return <LyricsWidget size={size} editing={editing} />
    case 'upnext':
      return <UpNextWidget editing={editing} />
  }
}
